import { useLayoutEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import NotFound from "../NotFound";
import {
  ArticleCTA,
  ArticleHeader,
  ArticleLayout,
  ArticleFAQ,
  ArticleSection,
  ArticleTableOfContents,
  BackToArticles,
  DischargeColourGuide,
  EditorialDisclaimer,
  RedFlagBox,
  ReferenceList,
  RelatedArticles,
  WhatNotToDo,
} from "./components";
import { SITE_URL, getArticleBySlug } from "./articleData";

function slugify(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function buildStructuredData(article) {
  const categorySlug = slugify(article.category);

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "Zaviramed",
        url: SITE_URL,
        logo: `${SITE_URL}/logo_new_nobg.png`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
          {
            "@type": "ListItem",
            position: 2,
            name: "Articles",
            item: `${SITE_URL}/articles`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: article.category,
            item: `${SITE_URL}/articles?category=${categorySlug}`,
          },
          {
            "@type": "ListItem",
            position: 4,
            name: article.shortTitle || article.title,
            item: article.url,
          },
        ],
      },
      {
        "@type": "MedicalWebPage",
        "@id": `${article.url}#webpage`,
        name: article.title,
        description: article.metaDescription,
        url: article.url,
        about: {
          "@type": "MedicalCondition",
          name: article.medicalTopic || article.shortTitle || article.title,
        },
        reviewedBy: {
          "@type": "Organization",
          name: article.reviewer?.name || "Zaviramed Clinical Review Team",
        },
        publisher: { "@id": `${SITE_URL}/#organization` },
        mainEntity: { "@id": `${article.url}#article` },
      },
      {
        "@type": "Article",
        "@id": `${article.url}#article`,
        headline: article.title,
        description: article.metaDescription,
        image: article.image,
        datePublished: article.datePublished,
        dateModified: article.dateModified,
        author: {
          "@type": "Organization",
          name: article.author?.name || "Zaviramed Editorial Team",
        },
        reviewedBy: {
          "@type": "Organization",
          name: article.reviewer?.name || "Zaviramed Clinical Review Team",
        },
        publisher: { "@id": `${SITE_URL}/#organization` },
        articleSection: article.category,
        keywords: article.keywords.join(", "),
        mainEntityOfPage: { "@id": `${article.url}#webpage` },
      },
    ],
  };
}

function ArticleBlock({ block }) {
  if (!block) return null;

  if (block.type === "paragraph") {
    return <p>{block.text}</p>;
  }

  if (block.type === "list") {
    return (
      <ul>
        {(block.items || []).map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }

  if (block.type === "guide") {
    return <DischargeColourGuide items={block.items || []} />;
  }

  if (block.type === "avoid") {
    return <WhatNotToDo items={block.items || []} />;
  }

  if (block.type === "redFlags") {
    return <RedFlagBox items={block.items || []} />;
  }

  return null;
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  const structuredData = useMemo(
    () => (article ? buildStructuredData(article) : null),
    [article]
  );

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [slug]);

  if (!article) return <NotFound />;

  const ctasBySection = (article.sectionCtas || []).reduce((acc, cta) => {
    if (!cta.after) return acc;
    acc[cta.after] = cta;
    return acc;
  }, {});

  return (
    <ArticleLayout
      seo={{
        title: article.metaTitle,
        description: article.metaDescription,
        canonical: article.url,
        image: article.image,
        type: "article",
        publishedAt: article.datePublished,
        updatedAt: article.dateModified,
      }}
      structuredData={[structuredData]}
      stickyHref={article.whatsappUrl}
    >
      <main id="main-content" className="pb-20">
        <ArticleHeader article={article} consultationHref={article.whatsappUrl} />

        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <ArticleTableOfContents items={article.toc} />

          <article className="rounded-[30px] bg-white px-5 py-8  sm:px-9 lg:px-12">
            <BackToArticles />

            {article.sections.map((section) => (
              <div key={section.id}>
                <ArticleSection id={section.id} title={section.title}>
                  {(section.blocks || []).map((block, index) => (
                    <ArticleBlock
                      key={`${section.id}-${block.type}-${index}`}
                      block={block}
                    />
                  ))}
                </ArticleSection>

                {ctasBySection[section.id] && (
                  <ArticleCTA
                    {...ctasBySection[section.id]}
                    href={article.whatsappUrl}
                  />
                )}
              </div>
            ))}

            {article.bottomCta && (
              <ArticleCTA {...article.bottomCta} href={article.whatsappUrl} />
            )}

            {article.bottomReassurance && (
              <p className="mt-3 text-center text-sm font-black text-slate-500">
                {article.bottomReassurance}
              </p>
            )}

            <ArticleFAQ items={article.faqs} />
            {/* <RelatedArticles items={article.relatedArticles} /> */}
            <ReferenceList items={article.references} />
            <EditorialDisclaimer />
          </article>
        </div>
      </main>
    </ArticleLayout>
  );
}

